import 'intersection-observer';
import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    IntersectionObserver: typeof IntersectionObserver;
  }
}

/**
 * LISTENERS holds the callback functions for each observed element.
 */
const LISTENERS = new Map<Element, (isVisible: boolean) => void>();

/**
 * OBSERVER is a global IntersectionObserver singleton.
 * Since our use-case is simple enough we can get away with using one
 * IntersectionObserver rather than create a new one for each execution.
 *
 * It needs to be wrapped in a function so the pre-build process can complete.
 */
let OBSERVER: IntersectionObserver;
const getObserver = () => {
  if (OBSERVER) {
    return OBSERVER;
  }

  OBSERVER = new window.IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const listener = LISTENERS.get(entry.target);
        if (listener) {
          const isVisible = entry.intersectionRatio > 0;
          listener(isVisible);
        }
      });
    },
    {
      rootMargin: '10px',
      threshold: 0.1,
    }
  );

  return OBSERVER;
};

interface UseVisibilitySensorOptions {
  triggerOnce?: boolean;
}

interface UseVisibilitySensorHook {
  visibilityRef: React.MutableRefObject<Element | undefined>;
  isVisible: boolean;
}

const useVisibilitySensor = ({
  triggerOnce,
}: UseVisibilitySensorOptions = {}): UseVisibilitySensorHook => {
  const visibilityRef = useRef<Element>();
  const [isVisible, setIsVisible] = useState(Boolean(window.IntersectionObserver));

  useEffect(() => {
    const observer = getObserver();
    const element = visibilityRef.current;

    if (!window.IntersectionObserver || !element) {
      return;
    }

    observer.observe(element);

    LISTENERS.set(element, (visible: boolean) => {
      if (isVisible !== visible) {
        setIsVisible(visible);
      }

      if (visible && triggerOnce) {
        observer.unobserve(element);
        LISTENERS.delete(element);
      }
    });

    return () => {
      observer.unobserve(element);
    };
  }, [visibilityRef, setIsVisible, isVisible, triggerOnce]);

  return { visibilityRef, isVisible };
};

export default useVisibilitySensor;
